import type { DriverShipment } from "@khazzin/api-client";

/**
 * A stop's facts the Today list and the stop screen share (EP10-S16): its
 * delivery window's state, whether it is done, the hand-offs to the phone,
 * Maps and WhatsApp, and the driver's own order for a manual run.
 */

/** The delivery windows, as hours of the tenant's day (`03/04` §3, CR-025). */
export const WINDOWS = {
	morning: [9, 13],
	afternoon: [13, 17],
	evening: [17, 21],
} as const;

export type WindowName = keyof typeof WINDOWS;

/** `closing` is inside the window's last half hour; `missed` is past its end. */
export type WindowState = "open" | "closing" | "missed";

export const CLOSING_MINUTES = 30;

function isWindow(value: string | null | undefined): value is WindowName {
	return value != null && Object.hasOwn(WINDOWS, value);
}

/** Minutes since midnight of `now` on the clock of `timeZone`. */
function minuteOfDay(now: Date, timeZone: string): number {
	const parts = new Intl.DateTimeFormat("en-GB", {
		timeZone,
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
	}).formatToParts(now);
	const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
	return part("hour") * 60 + part("minute");
}

/**
 * The window a stop is due in, and how close it is to closing. Null for a
 * stop with no window, `any`, or a carrier slot, which have no clock to show.
 */
export function windowState(
	window: string | null | undefined,
	now: Date,
	timeZone: string,
): { name: WindowName; state: WindowState } | null {
	if (!isWindow(window)) {
		return null;
	}
	const left = WINDOWS[window][1] * 60 - minuteOfDay(now, timeZone);
	const state: WindowState = left <= 0 ? "missed" : left <= CLOSING_MINUTES ? "closing" : "open";
	return { name: window, state };
}

/**
 * Statuses a stop still has work in; everything else is done for the day.
 * `driver_assigned` is a pickup still to collect (EP03-S21).
 */
const OPEN = [
	"ready_for_pickup",
	"picked_up",
	"at_delivery_warehouse",
	"delivering",
	"driver_assigned",
];

export function isDone(status: string): boolean {
	return !OPEN.includes(status);
}

/** Whether a stop is a pickup to collect rather than a delivery (EP03-S21). */
export function isPickup(stop: Pick<DriverShipment, "kind">): boolean {
	return stop.kind === "pickup";
}

/** The cached day with one stop's status (and more) replaced: a queued outcome, shown at once. */
export function withStop<T extends { shipments: Record<string, DriverShipment> }>(
	day: T,
	id: string,
	change: Partial<DriverShipment>,
): T {
	const stop = day.shipments[id];
	return stop === undefined
		? day
		: { ...day, shipments: { ...day.shipments, [id]: { ...stop, ...change } } };
}

/** The phone number with only the digits a dialler and wa.me read. */
function digits(phone: string): string {
	return phone.replace(/\D/g, "");
}

export function callHref(phone: string): string {
	return `tel:${phone.trim().startsWith("+") ? "+" : ""}${digits(phone)}`;
}

/** A WhatsApp chat with the recipient, the greeting already typed. */
export function whatsappHref(phone: string, greeting: string): string {
	return `https://wa.me/${digits(phone)}?text=${encodeURIComponent(greeting)}`;
}

/**
 * Google Maps directions to the stop: to its pin when it has one, else to the
 * address as written — a landmark address Maps may still find.
 */
export function navigateHref(s: Pick<DriverShipment, "pin" | "address" | "cityName">): string {
	const destination = s.pin
		? `${s.pin.lat},${s.pin.lng}`
		: [s.address, s.cityName, "Lebanon"].filter(Boolean).join(", ");
	return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

/**
 * The run in the driver's own order: the stops they moved, in the order they
 * put them, then every other stop in the run's order. A stop that left the run
 * leaves the preference with it.
 */
export function inLocalOrder(
	stopIds: string[],
	preferred: readonly string[] | undefined,
): string[] {
	if (preferred === undefined) {
		return stopIds;
	}
	const kept = preferred.filter((id) => stopIds.includes(id));
	return [...kept, ...stopIds.filter((id) => !kept.includes(id))];
}

/** The order with one stop moved one place up (-1) or down (+1). */
export function moved(order: string[], id: string, by: -1 | 1): string[] {
	const from = order.indexOf(id);
	const to = from + by;
	if (from < 0 || to < 0) {
		// Past the end, splicing it back puts it where it was.
		return order;
	}
	const next = [...order];
	next.splice(from, 1);
	next.splice(to, 0, id);
	return next;
}
